import { RequestResultValue } from "../../common/api/requestResult";
import { PaginationResponse } from "../../models/paginationResponse";
import { Bid, BidHistoryReport } from "../../models/bid";
import {
    BidBookDepthReport,
    BidFillRateReport,
    BidFunnelReport,
    BidOrderConversionReport,
    BidPriceSpreadInfo,
    BidRiskInfo,
    BidSaleChannelReport,
    BidsAnalyticsBookDepthParams,
    BidsAnalyticsPeriodParams,
    BidsAnalyticsPeriodTakeParams,
    BidsAnalyticsRisksParams,
    BidsAnalyticsTakeParams,
    BidTimeStatistic,
    BidTopBuyerInfo,
    BidTopSkinInfo,
} from "../../models/bidAnalytics";
import { HttpService } from "../../common/api/httpService";
import JSONbig from "json-bigint";

const JSONbigNative = JSONbig({ useNativeBigInt: true });

function appendOptional(params: URLSearchParams, key: string, value: string | number | undefined | null) {
    if (value === undefined || value === null || value === "") {
        return;
    }
    params.set(key, String(value));
}

async function getJson<T>(path: string): Promise<RequestResultValue<T>> {
    const response = await HttpService.getInstance().requestGetAsync(path);
    const text = await response.text();
    // json-bigint + useNativeBigInt падает на double (fill-rate, conversion и т.п.)
    let body: T;
    try {
        body = JSONbigNative.parse(text) as T;
    } catch {
        body = JSON.parse(text) as T;
    }
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<T>(true, body);
    }
    return new RequestResultValue<T>(false);
}

function periodQuery(params: BidsAnalyticsPeriodParams): string {
    const q = new URLSearchParams();
    q.set("from", params.from.toISOString());
    q.set("to", params.to.toISOString());
    appendOptional(q, "gameId", params.gameId);
    return q.toString();
}

export async function bidsListApi(
    page: number,
    count: number,
    gameId: number,
    userId: bigint,
    isActiveOnly: boolean,
    hashName?: string,
    bidId?: number
): Promise<RequestResultValue<PaginationResponse<Bid>>> {
    const response = await HttpService.getInstance().requestPostAsync(
        `admin/bids`,
        JSONbig.stringify({
            page: page,
            count: count,
            userId: userId > -1 ? userId : undefined,
            gameId: gameId > -1 ? gameId : undefined,
            hashName: hashName,
            activeOnly: isActiveOnly,
            bidId: bidId != null && bidId > 0 ? bidId : undefined,
        })
    );
    const text = await response.text();
    const body = JSONbigNative.parse(text);
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<PaginationResponse<Bid>>(true, body as PaginationResponse<Bid>);
    }
    return new RequestResultValue<PaginationResponse<Bid>>(false);
}

export async function bidsHistoryApi(bidId: number): Promise<RequestResultValue<BidHistoryReport>> {
    return getJson<BidHistoryReport>(`admin/Bids/${bidId}/history`);
}

export async function bidsTimeStatisticApi(
    params: BidsAnalyticsPeriodParams
): Promise<RequestResultValue<BidTimeStatistic[]>> {
    return getJson<BidTimeStatistic[]>(`admin/Bids/statistic?${periodQuery(params)}`);
}

export async function bidsFunnelApi(
    params: BidsAnalyticsPeriodParams
): Promise<RequestResultValue<BidFunnelReport>> {
    return getJson<BidFunnelReport>(`admin/Bids/funnel?${periodQuery(params)}`);
}

export async function bidsFillRateApi(
    params: BidsAnalyticsPeriodParams
): Promise<RequestResultValue<BidFillRateReport>> {
    return getJson<BidFillRateReport>(`admin/Bids/fill-rate?${periodQuery(params)}`);
}

export async function bidsTopSkinsApi(
    params: BidsAnalyticsPeriodTakeParams
): Promise<RequestResultValue<BidTopSkinInfo[]>> {
    const q = new URLSearchParams(periodQuery(params));
    appendOptional(q, "take", params.take ?? 20);
    return getJson<BidTopSkinInfo[]>(`admin/Bids/top-skins?${q.toString()}`);
}

export async function bidsPriceSpreadApi(
    params: BidsAnalyticsTakeParams
): Promise<RequestResultValue<BidPriceSpreadInfo[]>> {
    const q = new URLSearchParams();
    appendOptional(q, "take", params.take ?? 20);
    appendOptional(q, "gameId", params.gameId);
    return getJson<BidPriceSpreadInfo[]>(`admin/Bids/price-spread?${q.toString()}`);
}

export async function bidsBookDepthApi(
    params: BidsAnalyticsBookDepthParams = {}
): Promise<RequestResultValue<BidBookDepthReport>> {
    const q = new URLSearchParams();
    appendOptional(q, "gameId", params.gameId);
    if (params.from) {
        q.set("from", params.from.toISOString());
    }
    if (params.to) {
        q.set("to", params.to.toISOString());
    }
    const suffix = q.toString();
    return getJson<BidBookDepthReport>(`admin/Bids/book-depth${suffix ? `?${suffix}` : ""}`);
}

export async function bidsTopBuyersApi(
    params: BidsAnalyticsPeriodTakeParams
): Promise<RequestResultValue<BidTopBuyerInfo[]>> {
    const q = new URLSearchParams(periodQuery(params));
    appendOptional(q, "take", params.take ?? 20);
    return getJson<BidTopBuyerInfo[]>(`admin/Bids/top-buyers?${q.toString()}`);
}

export async function bidsOrderConversionApi(
    params: BidsAnalyticsPeriodParams
): Promise<RequestResultValue<BidOrderConversionReport>> {
    return getJson<BidOrderConversionReport>(`admin/Bids/order-conversion?${periodQuery(params)}`);
}

export async function bidsSaleChannelsApi(
    params: BidsAnalyticsPeriodParams & { onlyCompleted: boolean }
): Promise<RequestResultValue<BidSaleChannelReport>> {
    const q = new URLSearchParams(periodQuery(params));
    q.set("onlyCompleted", String(params.onlyCompleted));
    return getJson<BidSaleChannelReport>(`admin/Bids/sale-channels?${q.toString()}`);
}

export async function bidsRisksApi(
    params: BidsAnalyticsRisksParams
): Promise<RequestResultValue<BidRiskInfo[]>> {
    const q = new URLSearchParams();
    appendOptional(q, "take", params.take ?? 50);
    appendOptional(q, "stuckHours", params.stuckHours ?? 24);
    appendOptional(q, "gameId", params.gameId);
    return getJson<BidRiskInfo[]>(`admin/Bids/risks?${q.toString()}`);
}
